import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import {
  buildContext, clientRecipients, fillTemplate, mailtoHref, suggestedCategory,
} from "@/lib/billing";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, Mail } from "lucide-react";
import { toast } from "sonner";

const MAILTO_SAFE_LENGTH = 1800;

/**
 * Prepara um email a partir de um modelo, com os dados do cliente/fatura já
 * preenchidos. "Abrir no email" abre o programa de email do utilizador; se
 * houver fatura, o envio fica registado nela.
 */
export function EmailComposerDialog({ open, onOpenChange, client, invoice, templates = [], allInvoices = [], period, onLogged }) {
  const recipients = useMemo(() => clientRecipients(client), [client]);
  const [templateId, setTemplateId] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const template = templates.find((t) => t.id === templateId);
  const recipient = recipients.find((r) => r.email === to) || { name: "", email: to };

  useEffect(() => {
    if (!open) return;
    const category = suggestedCategory(invoice);
    const preferred = templates.find((t) => t.category === category) || templates[0];
    setTemplateId(preferred?.id || "");
    setTo(recipients[0]?.email || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id, client?.id, templates.length]);

  useEffect(() => {
    if (!open || !template) return;
    const context = buildContext({ client, invoice, recipient, allInvoices, period });
    setSubject(fillTemplate(template.subject, context));
    setBody(fillTemplate(template.body, context));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId, to, invoice?.id, client?.id]);

  const unfilled = `${subject}\n${body}`.match(/\{\w+\}/g);
  const tooLong = mailtoHref({ to, subject, body }).length > MAILTO_SAFE_LENGTH;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      toast.success("Assunto e texto copiados");
    } catch {
      toast.error("Não foi possível copiar — seleciona o texto à mão");
    }
  };

  const logSent = async () => {
    if (!invoice) return;
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/emails`, {
        templateId: template?.id || "",
        templateName: template?.name || "Personalizado",
        recipient: to,
      });
      onLogged?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEmail = async () => {
    if (!to) { toast.error("Indica o email do destinatário"); return; }
    window.location.href = mailtoHref({ to, subject, body });
    await logSent();
    toast.success(invoice ? `Email aberto e registado na fatura ${invoice.number}` : "Email aberto");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="email-composer">
        <DialogHeader>
          <DialogTitle className="font-poppins">Email para {client?.name}</DialogTitle>
          <DialogDescription>
            {invoice ? `Fatura ${invoice.number}` : "Sem fatura associada"} · revê o texto antes de enviar. O PDF da fatura tem de ser anexado no teu email.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-sm">Modelo</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Escolher modelo" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Para</Label>
            {recipients.length > 0 ? (
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Escolher contacto" /></SelectTrigger>
                <SelectContent>
                  {recipients.map((r) => <SelectItem key={r.email} value={r.email}>{r.name ? `${r.name} · ${r.email}` : r.email}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input className="mt-1" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Este cliente não tem contactos com email" />
            )}
          </div>
        </div>

        <div>
          <Label className="text-sm">Assunto</Label>
          <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm">Mensagem</Label>
          <Textarea className="mt-1 min-h-[220px] font-roboto text-sm" value={body} onChange={(e) => setBody(e.target.value)} />
        </div>

        {unfilled && (
          <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            Ainda há campos por preencher: {[...new Set(unfilled)].join(", ")}. Substitui-os à mão antes de enviar.
          </p>
        )}
        {tooLong && (
          <p className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
            Mensagem longa: alguns programas de email cortam o texto ao abrir. Se isso acontecer, usa &ldquo;Copiar texto&rdquo;.
          </p>
        )}

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={copy}><Copy className="mr-1.5 h-4 w-4" />Copiar texto</Button>
          <Button onClick={openEmail} className="bg-primary text-black hover:bg-primary/90" data-testid="email-open">
            <Mail className="mr-1.5 h-4 w-4" />Abrir no email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
